import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Divider,
  Flex,
  Grid,
  GridItem,
  Heading,
  Input,
  Spinner,
  Text,
  Alert,
  AlertIcon,
  useColorModeValue,
} from "@chakra-ui/react";
import { useTaskDetail } from "hooks/useTaskDetail";
import { getStatusLabel, getNextStatuses } from "utils/taskStatus";
import { formatCurrency } from "utils/formatCurrency";
import { formatDateTime } from "utils/formatDate";
import type { TaskStatus } from "types/fulfillment";
import { MdArrowBack } from "react-icons/md";
import Card from "components/card/Card";
import { Mark } from "components/icons/Mark";
import { OrderStatusBadge } from "views/partner/components/OrderStatusBadge";

export default function TaskDetail() {
  const { taskId } = useParams<{ taskId: string }>();
  const navigate = useNavigate();
  const { task, loading, error, updating, changeStatus } = useTaskDetail(taskId);
  const [trackingCode, setTrackingCode] = useState("");
  const [carrier, setCarrier] = useState("");
  const [statusError, setStatusError] = useState("");
  const [showShipForm, setShowShipForm] = useState(false);

  const headingColor = useColorModeValue("secondaryGray.900", "white");
  const mutedColor = useColorModeValue("secondaryGray.600", "secondaryGray.600");
  const borderColor = useColorModeValue("gray.100", "navy.700");

  if (loading) {
    return (
      <Flex justify="center" py={16}>
        <Spinner size="xl" color="brand.500" thickness="4px" />
      </Flex>
    );
  }

  if (error) {
    return (
      <Alert status="error" borderRadius="xl" flexDirection="column" py={6}>
        <AlertIcon />
        <Text mb={4}>{error}</Text>
        <Button size="sm" variant="link" colorScheme="brand" onClick={() => navigate(-1)}>
          ← Voltar
        </Button>
      </Alert>
    );
  }

  if (!task) return null;

  const nextStatuses = getNextStatuses(task.status);
  const addr = task.deliveryAddress;

  const handleStatusChange = async (newStatus: TaskStatus) => {
    if (newStatus === "shipped") {
      setShowShipForm(true);
      return;
    }
    setStatusError("");
    try {
      await changeStatus(newStatus);
    } catch (err: any) {
      setStatusError(err?.message ?? "Erro ao atualizar status.");
    }
  };

  const handleShipSubmit = async () => {
    setStatusError("");
    try {
      await changeStatus("shipped", {
        trackingCode: trackingCode.trim() || undefined,
        carrier: carrier.trim() || undefined,
      });
      setShowShipForm(false);
    } catch (err: any) {
      setStatusError(err?.message ?? "Erro ao atualizar status.");
    }
  };

  return (
    <Box>
      <Button
        leftIcon={<Mark as={MdArrowBack} />}
        variant="ghost"
        size="sm"
        mb={4}
        color={mutedColor}
        onClick={() => navigate(-1)}
      >
        Voltar
      </Button>

      <Flex mb={6} align="center" gap={4} flexWrap="wrap">
        <Heading size="lg" color={headingColor}>
          Pedido #{task.orderId?.slice(0, 8)}
        </Heading>
        <OrderStatusBadge status={task.status} />
        {taskId ? (
          <Button
            variant="link"
            colorScheme="brand"
            size="sm"
            ml={{ base: 0, md: 2 }}
            onClick={() =>
              navigate(`/partner/support?taskId=${encodeURIComponent(taskId)}`)
            }
          >
            Abrir chamado sobre este pedido
          </Button>
        ) : null}
      </Flex>

      <Grid templateColumns={{ base: "1fr", lg: "2fr 1fr" }} gap={6}>
        <GridItem>
          <Flex direction="column" gap={6}>
            <Card>
              <Heading size="md" color={headingColor} mb={4}>
                Itens do pedido
              </Heading>
              <Flex direction="column" gap={3}>
                {task.lines.map((line, i) => (
                  <Flex
                    key={i}
                    justify="space-between"
                    pb={3}
                    borderBottomWidth={i < task.lines.length - 1 ? "1px" : 0}
                    borderColor={borderColor}
                  >
                    <Box>
                      <Text fontWeight="medium" color={headingColor}>
                        {line.wineName}
                      </Text>
                      <Text fontSize="xs" color={mutedColor}>
                        {line.bottleSizeMl}ml • Qtd: {line.quantity}
                      </Text>
                    </Box>
                    <Box textAlign="right">
                      <Text fontSize="sm" color={mutedColor}>
                        {formatCurrency(line.unitPrice)} × {line.quantity}
                      </Text>
                      <Text fontWeight="medium" color={headingColor}>
                        {formatCurrency(line.subtotal)}
                      </Text>
                    </Box>
                  </Flex>
                ))}
              </Flex>
              <Divider my={4} />
              <Flex justify="space-between" align="center">
                <Text fontWeight="bold" color={headingColor}>
                  Subtotal
                </Text>
                <Text fontSize="lg" fontWeight="bold" color="brand.500">
                  {formatCurrency(task.subtotal)}
                </Text>
              </Flex>
            </Card>

            {nextStatuses.length > 0 && (
              <Card>
                <Heading size="md" color={headingColor} mb={4}>
                  Atualizar status
                </Heading>
                {statusError && (
                  <Alert status="error" borderRadius="md" mb={3} fontSize="sm">
                    <AlertIcon />
                    {statusError}
                  </Alert>
                )}
                {showShipForm ? (
                  <Flex direction="column" gap={3}>
                    <Input
                      placeholder="Código de rastreio (opcional)"
                      value={trackingCode}
                      onChange={(e) => setTrackingCode(e.target.value)}
                      variant="outline"
                    />
                    <Input
                      placeholder="Transportadora (opcional)"
                      value={carrier}
                      onChange={(e) => setCarrier(e.target.value)}
                      variant="outline"
                    />
                    <Flex gap={3}>
                      <Button
                        colorScheme="brand"
                        onClick={handleShipSubmit}
                        isLoading={updating}
                      >
                        Confirmar envio
                      </Button>
                      <Button variant="outline" onClick={() => setShowShipForm(false)}>
                        Cancelar
                      </Button>
                    </Flex>
                  </Flex>
                ) : (
                  <Flex gap={3} flexWrap="wrap">
                    {nextStatuses.map((s) => (
                      <Button
                        key={s}
                        colorScheme={s === "cancelled" ? "red" : "brand"}
                        variant={s === "cancelled" ? "outline" : "solid"}
                        onClick={() => handleStatusChange(s)}
                        isDisabled={updating}
                      >
                        {getStatusLabel(s)}
                      </Button>
                    ))}
                  </Flex>
                )}
              </Card>
            )}
          </Flex>
        </GridItem>

        <GridItem>
          <Flex direction="column" gap={6}>
            <Card>
              <Heading size="md" color={headingColor} mb={4}>
                Cliente
              </Heading>
              <Text color={headingColor}>{task.customer?.name || "—"}</Text>
              {task.customer?.phone && (
                <Text fontSize="sm" color={mutedColor} mt={2}>
                  {task.customer.phone}
                </Text>
              )}
            </Card>

            {addr && (
              <Card>
                <Heading size="md" color={headingColor} mb={4}>
                  Endereço de entrega
                </Heading>
                <Flex direction="column" gap={1} fontSize="sm" color={mutedColor}>
                  <Text>
                    {addr.street}
                    {addr.number ? `, ${addr.number}` : ""}
                  </Text>
                  {addr.complement && <Text>{addr.complement}</Text>}
                  <Text>{addr.neighborhood}</Text>
                  <Text>
                    {addr.city} — {addr.state}
                  </Text>
                  {addr.zipCode && <Text>CEP: {addr.zipCode}</Text>}
                </Flex>
              </Card>
            )}

            <Card>
              <Heading size="md" color={headingColor} mb={4}>
                Informações
              </Heading>
              <Flex direction="column" gap={2} fontSize="sm">
                <Flex justify="space-between">
                  <Text color={mutedColor}>Criado em</Text>
                  <Text color={headingColor}>{formatDateTime(task.createdAt)}</Text>
                </Flex>
                {task.shippedAt && (
                  <Flex justify="space-between">
                    <Text color={mutedColor}>Enviado em</Text>
                    <Text color={headingColor}>{formatDateTime(task.shippedAt)}</Text>
                  </Flex>
                )}
                {task.deliveredAt && (
                  <Flex justify="space-between">
                    <Text color={mutedColor}>Entregue em</Text>
                    <Text color={headingColor}>{formatDateTime(task.deliveredAt)}</Text>
                  </Flex>
                )}
                {task.trackingCode && (
                  <Flex justify="space-between">
                    <Text color={mutedColor}>Rastreio</Text>
                    <Text color={headingColor}>{task.trackingCode}</Text>
                  </Flex>
                )}
                {task.carrier && (
                  <Flex justify="space-between">
                    <Text color={mutedColor}>Transportadora</Text>
                    <Text color={headingColor}>{task.carrier}</Text>
                  </Flex>
                )}
              </Flex>
            </Card>
          </Flex>
        </GridItem>
      </Grid>
    </Box>
  );
}
